/**
 * Image Processing Configuration
 *
 * Centralized configuration for image processing presets used across
 * different contexts (grid view, list view, detail view, etc.).
 *
 * All images are processed to maintain a uniform height while preserving
 * their original aspect ratio for the width.
 */
import type { ImageProcessingOptions } from '@/types/image';

/**
 * Default image processing configuration
 * Used as the base for all other presets
 */
export const DEFAULT_IMAGE_CONFIG: ImageProcessingOptions = {
  targetHeight: 400,
  minWidth: 200,
  maxWidth: 800,
  compressQuality: 0.85,
  compressFormat: 'webp',
  allowUpscale: false,
  backgroundColor: '#fdf8f6', // brand-50 warm terracotta
};

/**
 * Grid view configuration
 * Product cards in grid layout - square tiles with moderate height
 */
export const GRID_IMAGE_CONFIG: ImageProcessingOptions = {
  ...DEFAULT_IMAGE_CONFIG,
  targetHeight: 300,
  maxWidth: 600,
  compressQuality: 0.82,
};

/**
 * List view configuration
 * Product cards in list layout - smaller thumbnails
 */
export const LIST_IMAGE_CONFIG: ImageProcessingOptions = {
  ...DEFAULT_IMAGE_CONFIG,
  targetHeight: 200,
  maxWidth: 400,
  compressQuality: 0.80,
};

/**
 * Detail view configuration
 * Product detail page - larger images
 */
export const DETAIL_IMAGE_CONFIG: ImageProcessingOptions = {
  ...DEFAULT_IMAGE_CONFIG,
  targetHeight: 500,
  maxWidth: 800,
  compressQuality: 0.90,
};

/**
 * Thumbnail configuration
 * Small thumbnails for galleries and previews
 */
export const THUMBNAIL_CONFIG: ImageProcessingOptions = {
  ...DEFAULT_IMAGE_CONFIG,
  targetHeight: 100,
  maxWidth: 200,
  compressQuality: 0.75,
};

/**
 * Mobile grid configuration
 * Optimized for mobile viewports with smaller images
 */
export const MOBILE_GRID_CONFIG: ImageProcessingOptions = {
  ...DEFAULT_IMAGE_CONFIG,
  targetHeight: 250,
  maxWidth: 400,
  compressQuality: 0.78,
};

/**
 * Get the appropriate configuration based on viewport width
 */
export function getConfigForViewport(
  viewportWidth: number,
  layout: 'grid' | 'list' = 'grid'
): ImageProcessingOptions {
  if (viewportWidth < 640) {
    return layout === 'grid' ? MOBILE_GRID_CONFIG : LIST_IMAGE_CONFIG;
  }
  return layout === 'grid' ? GRID_IMAGE_CONFIG : LIST_IMAGE_CONFIG;
}

/**
 * Get configuration for a specific context
 */
export function getImageConfig(
  context: 'default' | 'grid' | 'list' | 'detail' | 'thumbnail' | 'mobile'
): ImageProcessingOptions {
  switch (context) {
    case 'grid':
      return GRID_IMAGE_CONFIG;
    case 'list':
      return LIST_IMAGE_CONFIG;
    case 'detail':
      return DETAIL_IMAGE_CONFIG;
    case 'thumbnail':
      return THUMBNAIL_CONFIG;
    case 'mobile':
      return MOBILE_GRID_CONFIG;
    default:
      return DEFAULT_IMAGE_CONFIG;
  }
}
